import { Component } from 'react';
import { observe, unobserve, raw, isObservable } from '@nx-js/observer-util';
export { observable as store } from '@nx-js/observer-util';

const tasks = new Set();
let isStopped = false;

function add(task) {
  if (isStopped) {
    tasks.add(task);
  } else {
    runTask(task);
  }
}

function remove(task) {
  tasks.delete(task);
}

// this replaces the passed function with a function
// that batches all of its callback arguments
function batchCallbacks(fn) {
  return function batchedCallbacks(...args) {
    const batchedArgs = args.map(arg => typeof arg === 'function' ? function (...args) {
      return batch(arg, this, args);
    } : arg);
    return fn.apply(this, batchedArgs);
  };
}

// bathes obj.onevent = fn like calls
function batchMethod(obj, method) {
  const descriptor = Object.getOwnPropertyDescriptor(obj, method);
  if (!descriptor) return;
  const newDescriptor = Object.assign({}, descriptor, {
    set(value) {
      const batched = typeof value === 'function' ? function (...args) {
        return batch(value, this, args);
      } : value;
      return descriptor.set.call(this, batched);
    }
  });
  Object.defineProperty(obj, method, newDescriptor);
}

// this runs the passed function and delays all re-renders
// until the function is finished running
function batch(fn, ctx, args) {
  try {
    isStopped = true;
    return fn.apply(ctx, args);
  } finally {
    tasks.forEach(runTask);
    tasks.clear();
    isStopped = false;
  }
}

function runTask(task) {
  task();
}

// try to find the global object
// it is window in the DOM and global in NodeJS and React Native
let globalObj;
if (typeof window !== 'undefined') {
  globalObj = window;
} else if (typeof global !== 'undefined') {
  globalObj = global;
}

// do a sync batching for the most common task sources
// this should be removed when React's own batching is improved in the future
if (globalObj) {
  globalObj.setTimeout = batchCallbacks(globalObj.setTimeout);
  globalObj.setInterval = batchCallbacks(globalObj.setInterval);
  if (globalObj.requestAnimationFrame) {
    globalObj.requestAnimationFrame = batchCallbacks(globalObj.requestAnimationFrame);
  }
  if (globalObj.requestIdleCallback) {
    globalObj.requestIdleCallback = batchCallbacks(globalObj.requestIdleCallback);
  }
  // eslint-disable-next-line
  Promise.prototype.then = batchCallbacks(Promise.prototype.then);
  // eslint-disable-next-line
  Promise.prototype.catch = batchCallbacks(Promise.prototype.catch);

  // this batches websocket event handlers
  if (globalObj.WebSocket) {
    const websocketHandlers = ['onopen', 'onmessage', 'onerror', 'onclose'];
    websocketHandlers.forEach(method => batchMethod(globalObj.WebSocket.prototype, method));
  }
}

// DOM event handlers and HTTP event handlers don't have to be batched
// event handlers are batched by React
// HTTP event handlers are usually wrapped by Promises, which is covered above

const COMPONENT = Symbol('owner component');
const DUMMY_STATE = {};

function view(Comp, { devtool: rawDevtool } = {}) {
  const isStatelessComp = !(Comp.prototype && Comp.prototype.isReactComponent);
  const BaseComp = isStatelessComp ? Component : Comp;

  const devtool = rawDevtool ? operation => rawDevtool(Object.assign({ Component: Comp }, operation)) : undefined;

  // return a HOC which overwrites render, shouldComponentUpdate and componentWillUnmount
  // it decides when to run the new reactive methods and when to proxy to the original methods
  class ReactiveHOC extends BaseComp {
    constructor(props, context) {
      super(props, context);

      this.state = this.state || {};
      this.state[COMPONENT] = this;

      // run a dummy setState to schedule a new render, avoid forceUpdate
      const updater = () => this.setState(DUMMY_STATE);

      // create a reactive render for the component
      this.render = observe(this.render, {
        scheduler: {
          add: () => add(updater),
          delete: () => remove(updater)
        },
        debugger: devtool,
        lazy: true
      });
    }

    render() {
      return isStatelessComp ? Comp(this.props, this.context) : super.render();
    }

    // react should trigger updates on prop changes, while easyState handles store changes
    shouldComponentUpdate(nextProps, nextState) {
      const { props, state } = this;

      // respect the case when user prohibits updates
      if (super.shouldComponentUpdate && !super.shouldComponentUpdate(nextProps, nextState)) {
        devtool && devtool({ type: 'render', renderType: 'blocked' });
        return false;
      }

      // return true if it is a reactive render or state changes
      if (state !== nextState) {
        devtool && devtool({ type: 'render', renderType: 'reactive' });
        return true;
      }

      // the component should update if any of its props shallowly changed value
      const keys = Object.keys(props);
      const nextKeys = Object.keys(nextProps);
      if (nextKeys.length !== keys.length || nextKeys.some(key => props[key] !== nextProps[key])) {
        devtool && devtool({
          type: 'render',
          renderType: 'normal',
          props: nextProps,
          oldProps: props
        });
        return true;
      }
      return false;
    }

    // add a custom deriveStoresFromProps lifecyle method
    static getDerivedStateFromProps(props, state) {
      if (super.deriveStoresFromProps) {
        // inject all local stores and let the user mutate them directly
        const stores = mapStateToStores(state);
        super.deriveStoresFromProps(props, ...stores);
      }
      // respect user defined getDerivedStateFromProps
      if (super.getDerivedStateFromProps) {
        return super.getDerivedStateFromProps(props, state);
      }
      return null;
    }

    componentWillUnmount() {
      // call user defined componentWillUnmount
      if (super.componentWillUnmount) {
        super.componentWillUnmount();
      }
      // clean up memory used by Easy State
      unobserve(this.render);
    }
  }

  ReactiveHOC.displayName = Comp.displayName || Comp.name;
  // static props are inherited by class components,
  // but have to be copied for function components
  if (isStatelessComp) {
    for (let key of Object.keys(Comp)) {
      ReactiveHOC[key] = Comp[key];
    }
  }

  return ReactiveHOC;
}

function mapStateToStores(state) {
  // find store properties and map them to their none observable raw value
  // to do not trigger none static this.setState calls
  // from the static getDerivedStateFromProps lifecycle method
  const component = state[COMPONENT];
  return Object.keys(component).map(key => component[key]).filter(isObservable).map(raw);
}

export { view, batch };
